import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, Layers, Mail, LogOut, ExternalLink, Loader2, Images, Home, Wrench, Users, UserRound, Star, Phone, BarChart3, DatabaseBackup, ListTree, GalleryHorizontal, FileText, PanelBottom } from "lucide-react";
import { api, clearToken, getToken } from "../../adminApi";

const GROUPS = [
  {
    label: "Katalog",
    links: [
      { to: "/admin/projeler", label: "Projeler", icon: LayoutGrid },
      { to: "/admin/koleksiyonlar", label: "Koleksiyonlar", icon: Layers },
      { to: "/admin/gorseller", label: "Görseller", icon: Images },
    ],
  },
  {
    label: "Site İçeriği",
    links: [
      { to: "/admin/icerik/menu", label: "Menü Yönetimi", icon: ListTree },
      { to: "/admin/icerik/anasayfa", label: "Anasayfa", icon: Home },
      { to: "/admin/icerik/anasayfa-slayder", label: "Anasayfa Slayder", icon: GalleryHorizontal },
      { to: "/admin/icerik/sayfalar", label: "Sayfa Metinleri", icon: FileText },
      { to: "/admin/icerik/hizmetler", label: "Hizmetler", icon: Wrench },
      { to: "/admin/icerik/hakkimizda", label: "Hakkımızda", icon: Users },
      { to: "/admin/icerik/ekip", label: "Ekip", icon: UserRound },
      { to: "/admin/icerik/referanslar", label: "Referanslar", icon: Star },
      { to: "/admin/icerik/footer", label: "Footer", icon: PanelBottom },
      { to: "/admin/icerik/iletisim", label: "İletişim", icon: Phone },
    ],
  },
  {
    label: "Gelen Kutusu",
    links: [
      { to: "/admin/mesajlar", label: "Mesajlar", icon: Mail, badge: true },
      { to: "/admin/analitik", label: "Analitik", icon: BarChart3 },
      { to: "/admin/yedek", label: "Yedekleme", icon: DatabaseBackup },
    ],
  },
];

export default function AdminLayout() {
  const [state, setState] = useState("checking");
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (state !== "ok") return undefined;
    const refresh = () => api.unreadCount().then((r) => setUnread(r.unread)).catch(() => {});
    refresh();
    window.addEventListener("hrd-unread-changed", refresh);
    const timer = setInterval(refresh, 60000);
    return () => {
      window.removeEventListener("hrd-unread-changed", refresh);
      clearInterval(timer);
    };
  }, [state]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/giris", { replace: true });
      return;
    }
    api
      .me()
      .then(() => setState("ok"))
      .catch(() => {
        clearToken();
        navigate("/admin/giris", { replace: true });
      });
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin/giris", { replace: true });
  };

  if (state !== "ok") {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#8C7862]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] lg:flex" data-testid="admin-shell">
      <aside className="lg:w-[248px] lg:min-h-screen bg-white border-b lg:border-b-0 lg:border-r border-[#E2E0D8] lg:sticky lg:top-0 flex flex-col">
        <div className="px-6 py-6 border-b border-[#E2E0D8]">
          <Link to="/admin/projeler" className="flex items-baseline gap-2.5">
            <span className="display text-[26px] text-[#141414] leading-none">HRD</span>
            <span className="mono text-[8px] text-[#8C7862]">YÖNETİM</span>
          </Link>
        </div>

        <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible p-3 gap-1 flex-1">
          {GROUPS.map((group) => (
            <div key={group.label} className="flex lg:flex-col lg:mb-3 gap-1">
              <span className="hidden lg:block mono text-[8px] text-[#A8A29A] px-4 pt-3 pb-1.5">
                {group.label.toUpperCase()}
              </span>
              {group.links.map(({ to, label, icon: Icon, badge }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`nav-${to.split("/").pop()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 mono text-[9.5px] whitespace-nowrap transition-colors duration-300 ${
                      isActive ? "bg-[#141414] text-[#FAFAFA]" : "text-[#141414] hover:bg-[#F2F0EB]"
                    }`
                  }
                >
                  <Icon size={13} strokeWidth={1.7} />
                  {label.toUpperCase()}
                  {badge && unread > 0 && (
                    <span
                      data-testid="sidebar-unread-badge"
                      className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#8C7862] text-[#FAFAFA] mono text-[8px]"
                    >
                      {unread}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-[#E2E0D8] flex lg:flex-col gap-1">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-4 py-3 mono text-[10px] text-[#141414] hover:bg-[#F2F0EB] transition-colors"
          >
            <ExternalLink size={14} strokeWidth={1.7} /> SİTEYİ AÇ
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex items-center gap-3 px-4 py-3 mono text-[10px] text-[#8C2E1D] hover:bg-[#FBF1EE] transition-colors"
          >
            <LogOut size={14} strokeWidth={1.7} /> ÇIKIŞ YAP
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 px-6 lg:px-12 py-10 lg:py-14">
        <Outlet />
      </main>
    </div>
  );
}
